import React from "react";
import { Check, Star, Sparkles, ArrowRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import Reveal from "../Reveal";

const TIER_ID_TO_NAME_KEY = {
  starter: "tier_starter_name",
  pro: "tier_pro_name",
  premium: "tier_premium_name",
};
const TIER_ID_TO_DESC_KEY = {
  starter: "tier_starter_desc",
  pro: "tier_pro_desc",
  premium: "tier_premium_desc",
};

export default function PricingTiers({ pricing = [], content, onSelect }) {
  const { t, lang } = useLanguage();
  const { format: formatPrice } = useCurrency();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-cream py-section sm:py-section-lg border-t border-mist"
    >
      <div className="container-page">
        <Reveal as="div" className="max-w-2xl">
          <span className="eyebrow"><Sparkles className="h-3 w-3 text-gold" />{content?.pricing_eyebrow || t("service.pricing_eyebrow")}</span>
          <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
            {content?.pricing_title || t("service.pricing_title")}
          </h2>
          <p className="mt-5 text-sm text-ink/55">
            {content?.pricing_note || t("service.pricing_note")}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 lg:grid-cols-3" data-testid="pricing-grid">
          {pricing.map((tier) => {
            const name = content?.[TIER_ID_TO_NAME_KEY[tier.id]] || tier.id;
            const desc = content?.[TIER_ID_TO_DESC_KEY[tier.id]] || "";
            // Prefer language-specific features, then generic features, then content fields
            const features =
              (tier[`features_${lang}`] && tier[`features_${lang}`].length > 0)
                ? tier[`features_${lang}`]
                : (tier.features && tier.features.length > 0)
                ? tier.features
                : Array.from({ length: 6 })
                    .map((_, i) => content?.[`${tier.id}_feature_${i + 1}`])
                    .filter(Boolean);
            const popular = tier.popular;

            return (
              <Reveal
                as="div"
                key={tier.id}
                delay={popular ? 0.04 : 0}
                className={
                  "relative rounded-2xl border p-7 sm:p-8 flex flex-col bg-white transition-[transform,border-color,box-shadow] duration-500 ease-out-quart hover:-translate-y-1 " +
                  (popular
                    ? "border-gold shadow-[0_18px_50px_-22px_rgba(212,175,55,0.6)]"
                    : "border-mist hover:border-gold/40 hover:shadow-[0_24px_60px_-32px_rgba(10,10,10,0.25)]")
                }
                data-testid={`pricing-tier-${tier.id}`}
              >
                {popular ? (
                  <span
                    className="absolute -top-3 start-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-ink text-cream px-3.5 py-1 text-[0.65rem] font-medium uppercase tracking-[0.18em]"
                    data-testid="pricing-pro-badge"
                  >
                    <Star className="h-3 w-3 fill-gold text-gold" />
                    {content?.pricing_pro_badge || t("service.pricing_pro_badge")}
                  </span>
                ) : null}

                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.22em] text-ink/55">{name}</p>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{desc}</p>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-semibold text-ink" data-testid={`pricing-price-${tier.id}`}>
                      {formatPrice(tier.price_try)}
                    </span>
                    <span className="text-sm text-ink/50 ms-2">{t("service.pricing_per_month")}</span>
                  </div>
                </div>

                <ul className="mt-7 space-y-3 flex-1">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-ink/80">
                      <span className="flex-shrink-0 mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gold/15 text-gold">
                        <Check className="h-3 w-3" strokeWidth={2.5} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => onSelect && onSelect(tier)}
                  className={(popular ? "btn-gold" : "btn-outline-ink") + " mt-8 w-full group"}
                  data-testid={`pricing-cta-${tier.id}`}
                >
                  <span>{content?.pricing_cta || t("service.pricing_cta")}</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
